import { Box, DialogContent, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import DialogLayout from '@hapana/shared/web/components/shared/dialog/dialog-layout/dialog-layout.component'
import {
  CLOSE_TYPES,
  SIZES,
} from '@hapana/shared/web/components/shared/dialog/dialog-layout/dialog-layout.constants'
import { useDialog } from '@hapana/shared/web/components/shared/dialog/dialog.hooks'
import ModalGroup from '@hapana/shared/web/components/shared/modal-group/modal-group.component'
import RichContentEditor from '@hapana/shared/web/components/shared/rich-content-editor/rich-content-editor.component'
import TextField from '@hapana/shared/web/components/shared/text-field/text-field.component'
import useSiteStore from '_stores/site.store'
import { useRequestMaintenance, useEquipmentDiagnostics } from '_hooks/equipments.hooks'
import { useSiteEnableTanningFeatures } from '_hooks/sites.hooks'
import Loading from '@hapana/shared/web/components/shared/loading/loading.component'

import { EquipmentImage } from '_components/check-in-widget/equipment-image/equipment-image.component'
import {
  REQUEST_MAINTENANCE_DEFAULT_VALUES,
  REQUEST_MAINTENANCE_FIELDS,
} from './request-maintenance-dialog.constants'
import { styles } from './request-maintenance-dialog.styles'

export const RequestMaintenanceDialog = ({ equipment }) => {
  const { t } = useTranslation('checkIn')
  const { popDialog, setDialogState, filterDialogStack } = useDialog()
  const siteId = useSiteStore((state) => state.currentSite?.siteId)
  const { data: tanningFeatures } = useSiteEnableTanningFeatures()
  const hasExternalAgent = !!tanningFeatures?.integrations?.tanning?.externalApiUrl
  const { data: diagnostics, isLoading: isDiagnosticsLoading } = useEquipmentDiagnostics(siteId, {
    enabled: !!siteId && hasExternalAgent,
  })
  const { mutate: requestMaintenance, isLoading } = useRequestMaintenance({
    onSuccess: () => {
      setDialogState({ defaultTab: '3' })
      filterDialogStack('title', 'checkIn')
    },
  })

  const formMethods = useForm({
    defaultValues: REQUEST_MAINTENANCE_DEFAULT_VALUES,
    mode: 'onTouched',
  })

  const onSubmit = (data) => {
    requestMaintenance({
      siteId,
      equipmentId: equipment.equipmentId,
      subject: data[REQUEST_MAINTENANCE_FIELDS.subject.name],
      bodyHtml: data[REQUEST_MAINTENANCE_FIELDS.body.name],
    })
  }

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(onSubmit)}>
        <DialogLayout
          closeType={CLOSE_TYPES.TEXT}
          closeAction={popDialog}
          title={t('checkIn:equipments.requestMaintenance.title')}
          size={SIZES.SM}
          primaryAction={{
            label: t('equipments.requestMaintenance.sendMessage'),
            props: { type: 'submit', isLoading },
          }}
        >
          <DialogContent sx={styles.dialogContent}>
            <ModalGroup title={t('checkIn:equipments.requestMaintenance.equipmentSection')}>
              <Box sx={styles.equipmentRow}>
                <EquipmentImage
                  src={equipment.image}
                  name={equipment.name}
                  sx={styles.equipmentImage}
                />
                <Box sx={styles.equipmentInfo}>
                  <Typography typography="header4" color="gray.80">
                    {equipment.name}
                  </Typography>
                  <Box sx={styles.equipmentMeta}>
                    <Typography typography="medium" color="gray.64">
                      {t('checkIn:equipments.requestMaintenance.manufacturer')}{' '}
                      {equipment.manufacturer}
                    </Typography>
                    <Typography typography="medium" color="gray.12">
                      &bull;
                    </Typography>
                    <Typography typography="medium" color="gray.64">
                      {t('checkIn:equipments.requestMaintenance.equipmentId')} {equipment.timerId}
                    </Typography>
                  </Box>
                  {isDiagnosticsLoading && (
                    <Box mt={1}>
                      <Loading />
                    </Box>
                  )}
                  {diagnostics && (
                    <Box>
                      <Typography typography="medium" color="gray.64">
                        {t('checkIn:equipments.requestMaintenance.diagnostics.commPort')}{' '}
                        {diagnostics.commPort || 'N/A'}
                      </Typography>
                      <Typography typography="medium" color="gray.64">
                        {t('checkIn:equipments.requestMaintenance.diagnostics.healthy')}{' '}
                        {diagnostics.healthy
                          ? t('checkIn:equipments.requestMaintenance.diagnostics.healthy_true')
                          : t('checkIn:equipments.requestMaintenance.diagnostics.healthy_false')}
                      </Typography>
                      <Typography typography="medium" color="gray.64">
                        {t('checkIn:equipments.requestMaintenance.diagnostics.connected')}{' '}
                        {diagnostics.connected
                          ? t('checkIn:equipments.requestMaintenance.diagnostics.connected_true')
                          : t('checkIn:equipments.requestMaintenance.diagnostics.connected_false')}
                      </Typography>
                      <Typography typography="medium" color="gray.64">
                        {t('checkIn:equipments.requestMaintenance.diagnostics.maxRooms')}{' '}
                        {diagnostics.maxRooms || 'N/A'}
                      </Typography>
                      <Typography typography="medium" color="gray.64">
                        {t('checkIn:equipments.requestMaintenance.diagnostics.version')}{' '}
                        {diagnostics.version || 'N/A'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </ModalGroup>

            <ModalGroup title={t('checkIn:equipments.requestMaintenance.configureMessageSection')}>
              <Box>
                <Box>
                  <TextField
                    name={REQUEST_MAINTENANCE_FIELDS.subject.name}
                    id={REQUEST_MAINTENANCE_FIELDS.subject.id}
                    label={t(REQUEST_MAINTENANCE_FIELDS.subject.label)}
                  />
                </Box>
                <Box mt={2}>
                  <Typography typography="small" color="gray.56" sx={styles.messageHelperText}>
                    {t(REQUEST_MAINTENANCE_FIELDS.body.label)}
                  </Typography>
                  <RichContentEditor name={REQUEST_MAINTENANCE_FIELDS.body.name} />
                </Box>
              </Box>
            </ModalGroup>
          </DialogContent>
        </DialogLayout>
      </form>
    </FormProvider>
  )
}

RequestMaintenanceDialog.propTypes = {
  equipment: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    manufacturer: PropTypes.string,
    image: PropTypes.string,
    timerId: PropTypes.number,
  }).isRequired,
}

export default RequestMaintenanceDialog
